// components/HtmlPaginated.tsx
import { Box, Image, Text } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

const A4_HEIGHT = 1122;
const VERTICAL_PADDING = 80;
const PAGE_NUMBER_SPACE = 30;
const SAFE_CONTENT_HEIGHT = A4_HEIGHT - VERTICAL_PADDING - PAGE_NUMBER_SPACE;

export const HtmlPaginated = ({
  html,
  isDownload,
  pageNumber = 1,
}: {
  html: string;
  isDownload: boolean;
  pageNumber?: number;
}) => {
  const fullRef = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState<string[]>([]);
  const { user, signinuser } = useSelector(
    (state: { common: any }) => state.common
  );

  useEffect(() => {
    if (!fullRef.current || !html) return;

    const contentDiv = fullRef.current;
    const children = Array.from(contentDiv.children);
    const tempPages: HTMLElement[][] = [];

    let currentPage: HTMLElement[] = [];
    let currentHeight = 0;

    children.forEach((child) => {
      const el = child as HTMLElement;
      const elHeight = el.offsetHeight;

      if (
        currentHeight + elHeight > SAFE_CONTENT_HEIGHT &&
        currentPage.length > 0
      ) {
        tempPages.push(currentPage);
        currentPage = [];
        currentHeight = 0;
      }

      currentPage.push(el.cloneNode(true) as HTMLElement);
      currentHeight += elHeight;
    });

    if (currentPage.length > 0) tempPages.push(currentPage);

    const resultPages = tempPages.map((els) =>
      els.map((el) => el.outerHTML).join("")
    );

    setPages(resultPages);
  }, [html]);

  if (!html) return null;

  return (
    <>
      {/* Invisible measuring container */}
      <Box
        ref={fullRef}
        style={{
          visibility: "hidden",
          position: "absolute",
          zIndex: -9999,
          width: "714px",
          pointerEvents: "none",
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />

      {/* Rendered pages */}
      {pages.map((page, i) => (
        <Box
          key={i}
          position="relative"
          w="794px"
          h="1122px"
          bg="white"
          boxShadow="sm"
          className="page"
          overflow="hidden"
        >
          {/* More Visible Background */}
          <Image
            src={signinuser?.ComapanyImageTwo}
            alt="Background"
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            w="70%"
            h="auto"
            objectFit="contain"
            opacity={0.08}
            zIndex={0}
            pointerEvents="none"
            crossOrigin="anonymous"
          />

          {/* Content with tighter line height */}
          <Box
            position="relative"
            zIndex={1}
            px={10}
            py={10}
            pb={12}
            h="full"
            dangerouslySetInnerHTML={{ __html: page }}
            sx={{
              "& p": {
                mb: 2,
                lineHeight: "1.4",
                fontSize: "13px",
                color: "gray.700",
              },
              "& h1, & h2, & h3, & h4, & h5, & h6": {
                fontWeight: "700",
                color: "gray.900",
                mb: 2,
                mt: 3,
                lineHeight: "1.2",
              },
              "& h1": { fontSize: "20px" },
              "& h2": { fontSize: "18px" },
              "& h3": { fontSize: "16px" },
              "& h4, & h5, & h6": { fontSize: "14px" },
              "& ul, & ol": { pl: 5, mb: 2 },
              "& li": { mb: 1, fontSize: "13px", lineHeight: "1.4" },
              "& strong": { fontWeight: "700", color: "gray.900" },
              "& a": { color: "blue.600", textDecoration: "underline" },
              "& img": { maxW: "100%", borderRadius: "md", my: 3 },
              "& table": {
                width: "100%",
                borderCollapse: "collapse",
                mb: 3,
                fontSize: "12px",
              },
              "& th, & td": {
                border: "1px solid",
                borderColor: "gray.300",
                padding: "6px 8px",
              },
              "& th": { bg: "gray.100", fontWeight: "600" },
            }}
          />

          {/* Page Number */}
          <Text
            position="absolute"
            bottom={3}
            right={6}
            fontSize="10px"
            color="gray.400"
            zIndex={2}
            fontWeight="500"
          >
            {pageNumber + i}
          </Text>
        </Box>
      ))}
    </>
  );
};

export default HtmlPaginated;
